import React, { useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { ChatMessage as ChatMessageType, ProductData } from '../types/askStanley.types';
import { createIconElement } from '@/lib/utils/createIconElement';
import { PRODUCT_TYPES, stanAnalytics } from '@/lib';
import { useAskStanley } from '../hooks/useAskStanley';
import { parseMessageParts } from '../logic/messageParser';
import OnboardingStanley from '@/assets/icons/onboarding-stanley.svg';
import { ProductDisplay } from './ProductDisplay';
import { ChatMainFeedPost } from './ChatMainFeedPost';
import { ErrorDisplay } from './ErrorDisplay';
import { SuggestionsDisplay } from './SuggestionsDisplay';
import { LinksDisplay } from './LinksDisplay';
import { CodeBlockDisplay } from './CodeBlockDisplay';
import { FileAttachmentPreview } from './FileAttachmentPreview';
import { DisplayText } from './DisplayText';
import { MessageActions } from './MessageActions';
import { useUser } from '@/queries/useUser';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useStanRouter } from '@/hooks/useStanRouter';
import { ASK_STANLEY_CONSTANTS } from '../constants/askStanley.constants';
import styles from './ChatMessage.module.css';

interface ChatMessageProps {
    message: ChatMessageType;
    onPreviewImage: (file: File) => void;
}

export const ChatMessage = ({ message, onPreviewImage }: ChatMessageProps) => {
    // Access store for display state only
    const { isLoading, status, setGeneratedPageData, currentChatId } = useAskStanley();
    const { user } = useUser();
    const isReceived = message.type === ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.RECEIVED;
    const isSent = message.type === ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.SENT;
    const isStatus = message.type === ASK_STANLEY_CONSTANTS.CHAT_MESSAGE_TYPES.STATUS;
    const showThinkingIndicator = isReceived && isLoading && !message.content;
    const isMobile = useIsMobile();
    const { push } = useStanRouter();

    // Parse message content for embedded markers
    const processedContent = useMemo(() => {
        if (isSent || isStatus) {
            // For sent messages and status messages, just return the content as-is
            return [{ type: ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT, content: message.content }];
        }
        return parseMessageParts(message);
    }, [message, isSent, isStatus]);

    // Don't render status messages (we show thinking indicator instead)
    if (isStatus) {
        return null;
    }

    const ProductClicked = (product: ProductData | undefined) => {
        if (product) {
            setGeneratedPageData(product);
            stanAnalytics('chat-v2-product-card-clicked', {
                meta: { user_id: user?.user_id, username: user?.username },
                props: {
                    chat_id: currentChatId,
                    product_id: product.id,
                    product_title: product.product?.title,
                    product_generation_ulid: product.ulid,
                    product_type: product.type,
                    platform: isMobile ? 'mobile' : 'desktop',
                },
            });
            const productType = product.type || PRODUCT_TYPES.DIGITAL_DOWNLOAD;
            push(`/stores/default/page/${productType}/create?generated=true`);
        }
    };

    return (
        <div className={cn('flex w-full flex-col', isReceived ? 'items-start' : 'items-end')}>
            {/* AI Message Header */}
            {isReceived && !isStatus && (
                <div className='mb-4 flex items-start gap-2'>
                    {/* Stanley Icon - only show when not thinking */}
                    {!showThinkingIndicator && (
                        <div className='text-brand-soft flex size-6 items-center justify-center'>
                            {createIconElement(OnboardingStanley, { className: 'size-5' })}
                        </div>
                    )}

                    {/* Thinking Indicator */}
                    {showThinkingIndicator && (
                        <div className='flex items-center'>
                            <div className='text-gray leading-tight'>
                                <span className='from-brand-main to-brand-soft animate-pulse bg-gradient-to-r bg-clip-text text-transparent'>
                                    {status}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Message Content */}
            <div className={cn('max-w-full px-4 py-3', isSent ? 'max-w-3/4' : 'w-full', styles.chatMessageContent)}>
                {processedContent.map((part, index) => (
                    <div key={index}>
                        {part.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.PRODUCT && part.product && (
                            <div className='mt-2 w-full'>
                                <ProductDisplay
                                    product={part.product}
                                    index={index}
                                    onClick={() => ProductClicked(part.product)}
                                />
                            </div>
                        )}
                        {part.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.INSTAGRAM_MAIN_FEED_POST && part.post && (
                            <div className='mt-2 w-full'>
                                <ChatMainFeedPost post={part.post} />
                            </div>
                        )}
                        {part.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT && part.content && (
                            <div
                                className={cn(
                                    'text-dark rounded-lg leading-tight whitespace-pre-wrap',
                                    isSent ? 'bg-canvas ml-auto w-fit px-4 py-3' : 'bg-transparent'
                                )}
                                dangerouslySetInnerHTML={{ __html: part.content }}
                            />
                        )}
                    </div>
                ))}

                {/* Attachments for sent messages */}
                {isSent && message.attachments && message.attachments.length > 0 && (
                    <FileAttachmentPreview
                        attachments={message.attachments}
                        onRemoveAttachment={() => {}} // No-op for sent messages
                        onPreviewImage={onPreviewImage}
                        showCloseButton={false} // No close button for sent messages
                        size='sm' // Smaller size for chat messages
                        className='mt-2 flex w-full flex-wrap justify-end'
                    />
                )}
            </div>

            {/* Errors */}
            {isReceived && message.errors && message.errors.length > 0 && (
                <div className='mt-2 w-full'>
                    <div className='caption-1-bold text-error mb-3'>Errors:</div>
                    <div className='space-y-2'>
                        {message.errors.map((error, index) => (
                            <ErrorDisplay key={index} error={error} />
                        ))}
                    </div>
                </div>
            )}

            {/* Suggestions */}
            {isReceived && message.suggestions && message.suggestions.length > 0 && (
                <SuggestionsDisplay suggestions={message.suggestions} />
            )}

            {/* Links */}
            {isReceived && message.links && message.links.length > 0 && <LinksDisplay links={message.links} />}

            {/* Code Blocks */}
            {isReceived && message.codeBlocks && message.codeBlocks.length > 0 && (
                <CodeBlockDisplay codeBlocks={message.codeBlocks} />
            )}

            {/* Display Text */}
            {isReceived && message.displayText && <DisplayText text={message.displayText} />}

            {/* Message Actions for AI messages */}
            {isReceived &&
                processedContent.some(
                    part => part.type === ASK_STANLEY_CONSTANTS.CONTENT_TYPES.TEXT && part.content
                ) && <MessageActions message={message} />}
        </div>
    );
};
